import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import {noop} from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  TransitionSource,
  TaskSchemaNodeSection,
  FlowchartEdge,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNode,
  FlowchartNode,
  isInteractiveTaskNode,
  getInteractiveTaskNodeDatas,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {getTransitionSourceByNodeIdPairKey} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import {getTransitionSourceForNodePairWithDefault} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionLookupUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {getExecutionNodeTypeIdentifierForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {ORANGE_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {findAllUpstreamMappableNodeIds, findAllDownstreamMappableNodeIds} from '../TraverseFlowchart';
import CollapsibleTransitionCard from './CollapsibleTransitionCard';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {TransitionMappingsTable} from './TransitionFieldInfoSection';

function getOptionTextForTransitionSource(transitionSource: TransitionSource): string {
  switch (transitionSource) {
    case TransitionSource.OUTPUTS:
      return 'outputs';
    case TransitionSource.INPUTS:
      return 'tasks';
    default:
      throw new Error(`Unexpected transition source: ${transitionSource}`);
  }
}

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

function EmptySettingsContent(props: {message: string}): JSX.Element {
  return (
    <div className={css(styles.emptyStateContainer)}>
      <div className={css(styles.placeholderText)}>{props.message}</div>
    </div>
  );
}

enum TransitionDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

interface TransitionSourceDescriptionProps {
  isRootSourceNode: boolean;
  isTargetOutputNode: boolean;
  transitionSource: TransitionSource;
  availableTransitionSources: Array<TransitionSource>;
  connectedNodeId: string;
  connectedNodeName: string;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource) => void;
  locked?: boolean;
}

function TransitionSourceDescription(props: TransitionSourceDescriptionProps): JSX.Element {
  const {
    isRootSourceNode,
    isTargetOutputNode,
    transitionSource,
    availableTransitionSources,
    connectedNodeId,
    connectedNodeName,
    handleTransitionSourceSelect,
    locked,
  } = props;

  if (isRootSourceNode) {
    return (
      <div className={css(styles.cardBody)}>
        <span>
          Uses <strong>tasks</strong> to create tasks &amp; populate fields
        </span>
      </div>
    );
  }

  return (
    <div className={css(styles.cardBody)}>
      <span>Use&nbsp;</span>
      <div className={css(styles.fieldSourceDropdownWrapper)}>
        <SelectDropdown
          dataTestAttrs={{
            component: 'TransitionSourceDropdown',
            element: 'root',
            key: connectedNodeName,
          }}
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE} value={transitionSource}>
              {availableTransitionSources.map((source) => (
                <Option
                  key={source}
                  value={source}
                  onSelect={(value: TransitionSource) => {
                    handleTransitionSourceSelect(connectedNodeId, value);
                  }}
                  isDisabled={locked}
                >
                  {getOptionTextForTransitionSource(source)}
                </Option>
              ))}
            </Menu>
          }
          displayName={getOptionTextForTransitionSource(transitionSource)}
        />
      </div>
      {isTargetOutputNode ? (
        <span>&nbsp;to populate fields</span>
      ) : (
        <span>&nbsp;to create tasks &amp; populate fields</span>
      )}
      {transitionSource === TransitionSource.INPUTS && (
        <div className={css(styles.warningMessage)}>
          <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
          <span className={css(styles.secondaryText)}>
            Limits field mappings to input fields from the previous step
          </span>
        </div>
      )}
    </div>
  );
}

interface TransitionCardMappingsProps {
  connectedNode: TaskSchemaNode;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
}

function TransitionCardMappings(props: TransitionCardMappingsProps): JSX.Element {
  const {connectedNode, edges, locked} = props;
  const context = NodeDetailsSidepanelContext.useContext();

  if (edges.length === 0) {
    return <div className={css(styles.emptyMappingsText)}>No field mappings configured yet.</div>;
  }

  return (
    <div className={css(styles.fieldTableWrapper)}>
      <TransitionMappingsTable
        selectedFieldId=""
        node={connectedNode}
        flowchart={context.interactiveFlowchart}
        flowchartTaskSchema={context.flowchartTaskSchema}
        allSchemasById={context.allSchemasById}
        onChangeCustomMappingData={context.onChangeCustomMappingData}
        onChangeSelectedFieldId={noop}
        locked={locked}
        validationErrors={[]}
        edges={edges}
        tableDescription=""
        showBorder={false}
      />
    </div>
  );
}

interface TransitionCardProps {
  direction: TransitionDirection;
  connectedNode: TaskSchemaNode;
  transitionSourceNode: TaskSchemaNode;
  isTargetOutputNode: boolean;
  transitionSource: TransitionSource;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource) => void;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
}

function TransitionCard(props: TransitionCardProps): JSX.Element {
  const {
    direction,
    connectedNode,
    transitionSourceNode,
    isTargetOutputNode,
    transitionSource,
    handleTransitionSourceSelect,
    edges,
    locked,
  } = props;

  const isRootSourceNode =
    direction === TransitionDirection.INBOUND &&
    connectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;

  const hasOutputSection = transitionSourceNode.data.sections.some(
    (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
  );
  const availableTransitionSources = hasOutputSection
    ? Object.values(TransitionSource)
    : [TransitionSource.INPUTS];

  return (
    <CollapsibleTransitionCard
      header={direction === TransitionDirection.INBOUND ? 'From' : 'To'}
      headerAdornment={
        <Chip text={connectedNode.data.nodeName} icon={connectedNode.data.nodeIcon} size={ChipSize.MINI} />
      }
      alwaysVisibleContent={
        <TransitionSourceDescription
          isRootSourceNode={isRootSourceNode}
          isTargetOutputNode={isTargetOutputNode}
          transitionSource={transitionSource}
          availableTransitionSources={availableTransitionSources}
          connectedNodeId={connectedNode.id}
          connectedNodeName={connectedNode.data.nodeName}
          handleTransitionSourceSelect={handleTransitionSourceSelect}
          locked={locked}
        />
      }
      fullWidthChildren={true}
    >
      <TransitionCardMappings connectedNode={connectedNode} edges={edges} locked={locked} />
    </CollapsibleTransitionCard>
  );
}

const DIRECTION_CONFIG: Record<
  TransitionDirection,
  {title: string; emptyMessage: string; preposition: string}
> = {
  [TransitionDirection.INBOUND]: {
    title: 'Inbound transitions',
    emptyMessage: 'Draw mappings to this task schema to configure them here.',
    preposition: 'into',
  },
  [TransitionDirection.OUTBOUND]: {
    title: 'Outbound transitions',
    emptyMessage: 'Draw mappings from this task schema to configure them here.',
    preposition: 'from',
  },
};

interface TransitionsSectionProps {
  direction: TransitionDirection;
  selectedNode: TaskSchemaNode;
  selectedNodeIcon?: BenchlingIconName;
  // TODO: Should handle transitions with multiple source nodes
  connectedNodes: Array<TaskSchemaNode>;
  transitionSources: Record<string, TransitionSource>;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource) => void;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
}

function TransitionsSection(props: TransitionsSectionProps): JSX.Element {
  const {
    direction,
    selectedNode,
    selectedNodeIcon,
    connectedNodes,
    transitionSources,
    handleTransitionSourceSelect,
    edges,
    locked,
  } = props;

  const {title, emptyMessage, preposition} = DIRECTION_CONFIG[direction];

  const dataFlowEdgesByNodePair = useMemo(() => {
    const map = new Map<string, Array<FlowchartEdge>>();
    edges.forEach((edge) => {
      if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
        return;
      }
      const key = getTransitionSourceByNodeIdPairKey(edge.source, edge.target);
      const existing = map.get(key);
      if (existing) {
        existing.push(edge);
      } else {
        map.set(key, [edge]);
      }
    });
    return map;
  }, [edges]);

  const selectedNodeIsOutputNode =
    isInteractiveTaskNode(selectedNode) &&
    selectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

  const transitionCardItems = useMemo(
    () =>
      connectedNodes.map((connectedNode) => {
        const [edgeSource, edgeTarget] =
          direction === TransitionDirection.INBOUND
            ? [connectedNode.id, selectedNode.id]
            : [selectedNode.id, connectedNode.id];

        const edgesForPairKey = getTransitionSourceByNodeIdPairKey(edgeSource, edgeTarget);
        const edgesForPair = dataFlowEdgesByNodePair.get(edgesForPairKey) ?? [];

        const transitionSourceNode = direction === TransitionDirection.INBOUND ? connectedNode : selectedNode;

        const isTargetOutputNode =
          direction === TransitionDirection.INBOUND
            ? selectedNodeIsOutputNode
            : connectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

        return {
          key: connectedNode.id,
          connectedNode,
          transitionSourceNode,
          isTargetOutputNode,
          transitionSource: transitionSources[connectedNode.id],
          edges: edgesForPair,
        };
      }),
    [
      connectedNodes,
      direction,
      selectedNode,
      selectedNodeIsOutputNode,
      dataFlowEdgesByNodePair,
      transitionSources,
    ]
  );

  return (
    <div className={css(styles.transitionGroup)}>
      <div className={css(styles.sectionTitle)}>{title}</div>
      {connectedNodes.length === 0 ? (
        <EmptySettingsContent message={emptyMessage} />
      ) : (
        <>
          <div className={css(styles.description)}>
            <span>Configure behavior and mappings for transitions {preposition}&nbsp;</span>
            <Chip text={selectedNode.data.nodeName} icon={selectedNodeIcon} size={ChipSize.MINI} />
          </div>
          {transitionCardItems.map((item) => (
            <TransitionCard
              key={item.key}
              direction={direction}
              connectedNode={item.connectedNode}
              transitionSourceNode={item.transitionSourceNode}
              isTargetOutputNode={item.isTargetOutputNode}
              transitionSource={item.transitionSource}
              handleTransitionSourceSelect={handleTransitionSourceSelect}
              edges={item.edges}
              locked={locked}
            />
          ))}
        </>
      )}
    </div>
  );
}

function areAllNodesInteractiveTaskNodes(nodes: Array<FlowchartNode>): nodes is Array<TaskSchemaNode> {
  return nodes.every((node) => isInteractiveTaskNode(node));
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element {
  const {selectedNode, interactiveFlowchart, onChangeTransitionSource, flowchartEditorConfigurator} =
    NodeDetailsSidepanelContext.useContext();
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();

  const {upstreamMappableNodeIds, inboundTransitionSources} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const taskNodes = getInteractiveTaskNodeDatas(interactiveFlowchart.nodes);
    const inboundTransitionSources: Record<string, TransitionSource> = {};
    for (const sourceNodeId of upstreamMappableNodeIds) {
      inboundTransitionSources[sourceNodeId] = getTransitionSourceForNodePairWithDefault({
        sourceNodeId,
        targetNodeId: selectedNode.id,
        nodes: taskNodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: taskNodes,
      });
    }
    return {upstreamMappableNodeIds, inboundTransitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionsById, ptdvPairToTransitionIdIndex]);

  const downstreamMappableNodeIds = useMemo(
    () => findAllDownstreamMappableNodeIds(interactiveFlowchart, selectedNode.id),
    [interactiveFlowchart, selectedNode.id]
  );

  const {nodes} = interactiveFlowchart;
  const upstreamNodes = Object.values(nodes).filter((node) => upstreamMappableNodeIds.includes(node.id));
  assert(areAllNodesInteractiveTaskNodes(upstreamNodes), 'Expected all connected nodes to be task nodes');

  const downstreamNodes = Object.values(nodes).filter((node) => downstreamMappableNodeIds.includes(node.id));
  assert(
    areAllNodesInteractiveTaskNodes(downstreamNodes),
    'Expected all downstream mappable nodes to be task nodes'
  );

  const outboundTransitionSources = useMemo(() => {
    const taskNodes = getInteractiveTaskNodeDatas(interactiveFlowchart.nodes);
    const sources: Record<string, TransitionSource> = {};
    for (const targetNodeId of downstreamMappableNodeIds) {
      sources[targetNodeId] = getTransitionSourceForNodePairWithDefault({
        sourceNodeId: selectedNode.id,
        targetNodeId,
        nodes: taskNodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: taskNodes,
      });
    }
    return sources;
  }, [
    interactiveFlowchart,
    selectedNode.id,
    downstreamMappableNodeIds,
    transitionsById,
    ptdvPairToTransitionIdIndex,
  ]);

  const handleTransitionSourceSelectForDirection =
    (direction: TransitionDirection) =>
    (connectedNodeId: string, value: TransitionSource): void => {
      const sources =
        direction === TransitionDirection.INBOUND ? inboundTransitionSources : outboundTransitionSources;
      const currentTransitionSource = sources[connectedNodeId];

      const [sourceId, targetId] =
        direction === TransitionDirection.INBOUND
          ? [connectedNodeId, selectedNode.id]
          : [selectedNode.id, connectedNodeId];
      const nodeIdPair = getTransitionSourceByNodeIdPairKey(sourceId, targetId);

      if (currentTransitionSource !== value) {
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.TRANSITION_SOURCE_CHANGED, {
          oldValue: currentTransitionSource,
          newValue: value,
          flowchartType: flowchartEditorConfigurator.flowchartTypeName,
          actionMethod: ActionMethod.BUTTON_CLICK,
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          nodeType: getExecutionNodeTypeIdentifierForNode(selectedNode),
        });
        onChangeTransitionSource(nodeIdPair, value);
      }
    };

  return (
    <div key={selectedNode.id}>
      <TransitionsSection
        direction={TransitionDirection.INBOUND}
        selectedNode={selectedNode as TaskSchemaNode}
        selectedNodeIcon={selectedNode.data.nodeIcon}
        connectedNodes={upstreamNodes}
        transitionSources={inboundTransitionSources}
        handleTransitionSourceSelect={handleTransitionSourceSelectForDirection(TransitionDirection.INBOUND)}
        edges={interactiveFlowchart.edges}
        locked={props.locked}
      />
      <TransitionsSection
        direction={TransitionDirection.OUTBOUND}
        selectedNode={selectedNode as TaskSchemaNode}
        selectedNodeIcon={selectedNode.data.nodeIcon}
        connectedNodes={downstreamNodes}
        transitionSources={outboundTransitionSources}
        handleTransitionSourceSelect={handleTransitionSourceSelectForDirection(TransitionDirection.OUTBOUND)}
        edges={interactiveFlowchart.edges}
        locked={props.locked}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  transitionGroup: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: 4,
  },
  description: {
    fontSize: 14,
    fontWeight: 400,
    color: Color.GRAY_900,
    marginBottom: 12,
  },
  cardBody: {
    fontSize: 14,
    color: Color.GRAY_900,
    paddingLeft: 16,
    paddingBottom: 12,
  },
  fieldTableWrapper: {
    borderTop: `1px solid ${Color.GRAY_200}`,
  },
  warningMessage: {
    marginTop: 4,
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  warningIcon: {
    paddingRight: 4,
    color: ORANGE_DARK,
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  placeholderText: {
    fontSize: 14,
    color: Color.GRAY_500,
  },
  fieldSourceDropdownWrapper: {
    display: 'inline-block',
  },
  emptyMappingsText: {
    color: Color.GRAY_600,
    fontSize: 14,
    padding: '0 16px 12px',
  },
});
